import { Check, ChevronDown } from 'lucide-react';
import {
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@wundo/ui';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectMenuProps {
  /** The facet, for screen readers — "Entity", "Action". */
  label: string;
  /** What the button counts — "entities", "actions". */
  plural: string;
  options: readonly MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * A filter that takes several values at once.
 *
 * `FilterSelect` answers "which one"; this answers "which of these", which the
 * change log needs because the useful question is almost always a handful of
 * entity types rather than one. Empty means everything, so the button says
 * "All entities" rather than "0 entities" — nothing picked is not a filter that
 * matches nothing.
 */
export function MultiSelectMenu({
  label,
  plural,
  options,
  value,
  onChange,
}: MultiSelectMenuProps) {
  function toggle(option: string) {
    onChange(
      value.includes(option) ? value.filter((one) => one !== option) : [...value, option],
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <span className='sr-only'>{label}</span>
      <Menu>
        <MenuTrigger render={<Button variant='quiet' size='sm' />}>
          {value.length === 0 ? `All ${plural}` : `${value.length} ${plural}`}
          <ChevronDown className='ml-1.5 size-2.5 stroke-3' aria-hidden='true' />
        </MenuTrigger>
        <MenuContent>
          <MenuItem
            closeOnClick={false}
            onClick={() => onChange(options.map((o) => o.value))}
          >
            Select all
          </MenuItem>
          <MenuItem closeOnClick={false} onClick={() => onChange([])}>
            None
          </MenuItem>
          <MenuSeparator />
          {options.map((option) => (
            <MenuItem
              key={option.value}
              closeOnClick={false}
              className='justify-between'
              onClick={() => toggle(option.value)}
            >
              {option.label}
              {value.includes(option.value) && (
                <Check className='size-3.25 stroke-3 text-ink' aria-hidden='true' />
              )}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
    </div>
  );
}
